import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { listConflicts, resolveConflict } from '../sync/controller';
import type { LocalConflict } from '../sync/local';

/** Pretty-print a stored JSON payload for side-by-side comparison. */
function preview(payload: string | null): string {
  if (payload == null) return '(deleted)';
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
}

/**
 * Manual conflict resolution: for each unresolved conflict the user keeps their
 * version, takes the cloud version, or edits a merged payload by hand. Both
 * versions are always shown so nothing is lost silently.
 */
export default function ConflictDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [conflicts, setConflicts] = useState<LocalConflict[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mergeText, setMergeText] = useState<Record<string, string>>({});

  // The dialog stays mounted with `open` toggling, so re-query on every open;
  // a mount-time snapshot would miss conflicts produced by later syncs.
  useEffect(() => {
    if (open) {
      setConflicts(listConflicts());
      setError(null);
      setMergeText({});
    }
  }, [open]);

  function refresh() {
    const next = listConflicts();
    setConflicts(next);
    if (next.length === 0) onClose();
  }

  async function resolve(c: LocalConflict, resolution: 'mine' | 'cloud' | 'merge') {
    setBusy(true);
    setError(null);
    try {
      const merged = resolution === 'merge' ? (mergeText[c.id] ?? c.serverPayload ?? '') : null;
      await resolveConflict(c.id, resolution, merged);
      refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Resolve sync conflicts</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {conflicts.length === 0 ? (
          <Typography color="text.secondary">No conflicts to resolve.</Typography>
        ) : (
          <Stack spacing={3}>
            {conflicts.map((c) => (
              <Box key={c.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {c.entityType} · {c.entityId}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This record was changed on another device and here at the same time. Choose which
                  to keep, or merge them.
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline">This device</Typography>
                    <Box component="pre" sx={preStyle}>
                      {preview(c.clientPayload)}
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline">Cloud</Typography>
                    <Box component="pre" sx={preStyle}>
                      {preview(c.serverPayload)}
                    </Box>
                  </Box>
                </Stack>
                <TextField
                  label="Merged (edit to combine both, then Merge)"
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                  sx={{ mt: 1.5 }}
                  value={mergeText[c.id] ?? c.serverPayload ?? ''}
                  onChange={(e) => setMergeText((m) => ({ ...m, [c.id]: e.target.value }))}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <Button size="small" variant="contained" disabled={busy} onClick={() => resolve(c, 'mine')}>
                    Keep mine
                  </Button>
                  <Button size="small" variant="outlined" disabled={busy} onClick={() => resolve(c, 'cloud')}>
                    Use cloud
                  </Button>
                  <Button size="small" disabled={busy} onClick={() => resolve(c, 'merge')}>
                    Merge manually
                  </Button>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

const preStyle = {
  m: 0,
  p: 1,
  bgcolor: 'action.hover',
  borderRadius: 1,
  fontSize: 12,
  maxHeight: 180,
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
} as const;
